import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Container from "components/__reusable/Container";
import Checkbox from "components/__reusable/Checkbox";
import UploadPictureStatus from "components/__reusable/UploadPictureStatus";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { getClassPublishReadinessOfAcademicYear } from "features/class/classAction";
import LoadingDialog from "components/__reusable/LoadingDialog";
import { UNPUBLISHED_PICTURES_PATH } from "router/pathName";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import PictureStatusDialog from "./PictureStatusDialog";
import type { Class } from "types/Class";
import RefreshIcon from "assets/icons/RefreshIcon";
import YearButton from "components/Class/YearButton";

const Summarization = () => {
  const { t } = useTranslation();

  const { classes, loading, viewedYear } = useAppSelector(
    (state) => state.class,
  );
  const dispatch = useAppDispatch();
  const fetchClasses = () =>
    dispatch(getClassPublishReadinessOfAcademicYear({ year: viewedYear }));
  const callFetchClasses = useCallback(fetchClasses, [dispatch, viewedYear]);
  useEffect(() => {
    callFetchClasses();
  }, [callFetchClasses]);

  const [selectedClasses, setSelectedClasses] = useState<Class[]>([]);

  // Clear selected classes when year changes
  useEffect(() => {
    setSelectedClasses([]);
  }, [viewedYear]);

  const selectAllClasses = (status: boolean) => {
    if (status) setSelectedClasses(classes);
    else setSelectedClasses([]);
  };
  const selectClass = (status: boolean, c: Class) => {
    if (status) {
      setSelectedClasses([...selectedClasses, c]);
    } else {
      const temp = [...selectedClasses];
      const index = temp.findIndex((cl) => cl.id === c.id);
      if (index >= 0) temp.splice(index, 1);
      setSelectedClasses(temp);
    }
  };

  const [isOpenPublishDialog, setIsOpenPublishDialog] = useState(false);

  return (
    <div className="h-full flex flex-col space-y-3">
      {loading && <LoadingDialog />}
      <div className="flex items-center space-x-10">
        <h2 className="text-xl font-semibold">
          {t("_unpublished_picture._current_status")}
        </h2>
        <div className="w-fit">
          <button
            className="flex items-center space-x-2 rounded border border-[#5B5B5B] bg-white px-3 py-2 text-[#5B5B5B] transition-colors duration-200 ease-out hover:bg-[#F4F4F4]"
            onClick={fetchClasses}
          >
            <RefreshIcon width={16} height={16} color={"#5B5B5B"} />
            <p>データを更新</p>
          </button>
        </div>
      </div>
      <div className="flex-1">
        <Container>
          <div className="h-full flex flex-col space-y-1">
            <div className=" mb-3">
              <div className="flex whitespace-nowrap space-x-3">
                <YearButton />
              </div>
            </div>
            <div className="flex-1 overflow-auto relative">
              <div className="w-full h-full absolute">
                {loading && <LoadingDialog />}
                {!loading && classes.length === 0 && (
                  <p>{t("_generic._no_data")}</p>
                )}
                {!loading && classes.length > 0 && (
                  <table className="w-full relative border border-gray-300">
                    <thead className="bg-gray-100 sticky top-0 z-10 border border-gray-300">
                      <tr className="text-center">
                        <th className="p-2 text-left border border-gray-300">
                          <div className="flex space-x-2">
                            <Checkbox
                              name={"all"}
                              label={""}
                              isChecked={
                                selectedClasses.length === classes.length
                              }
                              onChange={selectAllClasses}
                            />
                            <p className="flex-1 flex items-center">
                              {t("_unpublished_picture._class")}
                            </p>
                          </div>
                        </th>
                        <th className="p-2 whitespace-nowrap border border-gray-300">
                          {t("_unpublished_picture._upload")}
                        </th>
                        <th className="p-2 whitespace-nowrap border border-gray-300">
                          {t("_unpublished_picture._ai_tagging")}
                        </th>
                        <th className="p-2 whitespace-nowrap border border-gray-300">
                          {t("_unpublished_picture._checked")}
                        </th>
                        <th className="p-2 whitespace-nowrap border border-gray-300">
                          {t("_unpublished_picture._delivery")}
                        </th>
                        <th className="p-2 whitespace-nowrap border border-gray-300">
                          {t("_unpublished_picture._last_delivery_date")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {classes.map((c) => (
                        <tr key={c.id}>
                          <td className="p-2 border border-gray-300">
                            <div className="flex space-x-2">
                              <Checkbox
                                name={c.id.toString()}
                                label={""}
                                isChecked={
                                  selectedClasses.find(
                                    (cl) => cl.id === c.id,
                                  ) !== undefined
                                }
                                onChange={(status) => selectClass(status, c)}
                              />
                              <Link
                                to={`${UNPUBLISHED_PICTURES_PATH}/${c.id}`}
                                className="flex items-center flex-1 underline hover:no-underline"
                              >
                                {`${c.year} - ${c.name}`}
                              </Link>
                            </div>
                          </td>
                          <UploadPictureStatus
                            isUploaded={c.is_picture_uploaded ?? false}
                            isAICompleted={c.is_ai_completed ?? false}
                            remainingAIPictures={c.remain_ai_picture ?? 0}
                            isAllChecked={c.is_all_checked ?? false}
                            numberUncheckedPics={c.number_of_unchecked_pic ?? 0}
                            isDelivered={c.is_delivered ?? false}
                            lastDeliveryDate={c.last_delivery_datetime}
                          />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>
      {!loading && classes.length > 0 && (
        <PrimaryActionButton
          label={t("_unpublished_picture._publish")}
          disabled={selectedClasses.length === 0}
          onClick={() => setIsOpenPublishDialog(true)}
        />
      )}
      {isOpenPublishDialog && (
        <PictureStatusDialog
          classes={selectedClasses}
          onNoClick={() => setIsOpenPublishDialog(false)}
          onYesClick={() => {
            setSelectedClasses([]);
            setIsOpenPublishDialog(false);
          }}
        />
      )}
    </div>
  );
};

export default Summarization;
